import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronLeft } from 'lucide-react';
import SEOHead from '@/components/SEOHead';

const content = {
  'zh-TW': {
    category: 'B2B 建築科技與 AI',
    title: 'AI 如何在簽約前審查美國分包合約的條款風險',
    desc: '拆解 AI 如何解析分包合約，標記違約賠償金、彌償與付款條件等高風險條款。',
    date: '2026 年 6 月',
    s1_title: '藏在數百頁合約裡的百萬風險',
    s1: '一份主建築合約動輒數百頁，附帶圖則、規範與補充協議。逾期違約金（LD）條款、彌償條款、變更令程序、有條件付款條款 — 每一項義務都埋在密密麻麻的條文之間。漏看一條 pay-if-paid 付款條件，可能就等於承擔收不到款的風險；漏看一條轉嫁條款，利潤便在簽約當日已被蠶食。',
    s2_title: '從人手審閱到簽約前條款風險審查',
    s2: 'AI 合約掃描引擎會將整份合約逐條結構化：自動抽取每一項義務、期限與觸發條件，依風險程度標記條款，並為每項發現註明條款出處。團隊不再依賴某位資深商務經理的記憶，而是在簽署之前就看清風險所在。',
    s3_title: '結果：合約由負債變成導航',
    s3: '當合約風險被前置到簽約前，合約就從一份「出事才翻開的文件」變成談判的依據。這正是我們打造 Vowpace AI 的原因 — 讓分包商在每一份合約上，都站在資訊對等的一方。',
    cta_title: '體驗 AI 合約風險掃描',
    cta_btn: '了解 Vowpace AI',
    back: '返回洞察中心',
  },
  'zh-CN': {
    category: 'B2B 建筑科技与 AI',
    title: 'AI 如何在签约前审查美国分包合同的条款风险',
    desc: '拆解 AI 如何解析分包合同，标记违约赔偿金、弥偿与付款条件等高风险条款。',
    date: '2026 年 6 月',
    s1_title: '藏在数百页合同里的百万风险',
    s1: '一份主建筑合同动辄数百页，附带图纸、规范与补充协议。逾期违约金（LD）条款、弥偿条款、变更令程序、有条件付款条款 — 每一项义务都埋在密密麻麻的条文之间。漏看一条 pay-if-paid 付款条件，可能就等于承担收不到款的风险；漏看一条转嫁条款，利润便在签约当日已被蚕食。',
    s2_title: '从人手审阅到签约前条款风险审查',
    s2: 'AI 合同扫描引擎会将整份合同逐条结构化：自动抽取每一项义务、期限与触发条件，依风险程度标记条款，并为每项发现注明条款出处。团队不再依赖某位资深商务经理的记忆，而是在签署之前就看清风险所在。',
    s3_title: '结果：合同由负债变成导航',
    s3: '当合同风险被前置到签约前，合同就从一份「出事才翻开的文件」变成谈判的依据。这正是我们打造 Vowpace AI 的原因 — 让分包商在每一份合同上，都站在信息对等的一方。',
    cta_title: '体验 AI 合同风险扫描',
    cta_btn: '了解 Vowpace AI',
    back: '返回洞察中心',
  },
  en: {
    category: 'B2B ConTech & AI',
    title: 'How AI Reviews US Subcontract Clause Risk Before You Sign',
    desc: 'How AI parses subcontracts and flags high-risk clauses — liquidated damages, indemnities, and payment conditions.',
    date: 'June 2026',
    s1_title: 'Million-dollar risks buried in hundred-page contracts',
    s1: 'A main construction contract easily runs to hundreds of pages, plus drawings, specifications, and supplemental agreements. Liquidated damages clauses, indemnities, variation order procedures, pay-if-paid conditions — every obligation is buried deep in dense legal text. Miss a pay-if-paid condition and you may never be paid; overlook a single pass-through clause and your margin is eroded on the day you sign.',
    s2_title: 'From manual review to pre-signature clause-risk review',
    s2: 'An AI contract scanning engine structures the entire contract clause by clause: it automatically extracts every obligation, deadline, and trigger condition, grades each clause by risk severity, and cites the clause behind every finding. Teams no longer rely on one senior commercial manager\'s memory — the risks are visible before anyone signs.',
    s3_title: 'The result: contracts become navigation, not liability',
    s3: 'When contract risk is surfaced before signing, the contract transforms from "a document you open when things go wrong" into a basis for negotiation. This is exactly why we built Vowpace AI — so subcontractors stand on equal informational footing in every contract.',
    cta_title: 'Experience AI contract risk scanning',
    cta_btn: 'Discover Vowpace AI',
    back: 'Back to Insights',
  },
};

export default function InsightConstructionPage() {
  const { lang } = useLanguage();
  const c = content[lang] || content.en;

  return (
    <div data-testid="insight-construction-page" className="pt-20 bg-[#080B10]">
      <SEOHead path="/insights/construction-ai" />
      <section className="py-20 md:py-28 bg-[#0E131F]/50 border-b border-slate-800/60">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <span className="inline-block px-3 py-1 rounded-full bg-[#F59E0B]/10 border border-[#F59E0B]/30 text-xs font-bold text-[#F59E0B] tracking-wide mb-6">
            {c.category}
          </span>
          <h1
            data-testid="insight-title"
            className="text-3xl md:text-4xl lg:text-5xl tracking-tighter font-bold text-white leading-tight mb-6"
            style={{ fontFamily: "'Plus Jakarta Sans', 'Noto Sans TC', sans-serif" }}
          >
            {c.title}
          </h1>
          <p className="text-base md:text-lg text-[#94A3B8] leading-relaxed mb-6" style={{ fontFamily: "'Manrope', 'Noto Sans TC', sans-serif" }}>
            {c.desc}
          </p>
          <p className="text-sm text-white/70">{c.date} · DZent Limited</p>
        </div>
      </section>

      <article className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          {[['s1_title', 's1'], ['s2_title', 's2'], ['s3_title', 's3']].map(([ht, bt]) => (
            <section key={ht} className="mb-14">
              <h2 className="text-2xl md:text-3xl tracking-tight font-bold text-[#F8FAFC] mb-5"
                  style={{ fontFamily: "'Plus Jakarta Sans', 'Noto Sans TC', sans-serif" }}>
                {c[ht]}
              </h2>
              <p className="text-base md:text-lg text-[#94A3B8] leading-relaxed" style={{ fontFamily: "'Manrope', 'Noto Sans TC', sans-serif" }}>
                {c[bt]}
              </p>
            </section>
          ))}

          <section className="text-center py-12 border-t border-white/10">
            <h2 className="text-2xl md:text-3xl tracking-tight font-bold text-[#F8FAFC] mb-6"
                style={{ fontFamily: "'Plus Jakarta Sans', 'Noto Sans TC', sans-serif" }}>
              {c.cta_title}
            </h2>
            <Link to="/products/vowpace">
              <Button data-testid="insight-cta-button"
                      className="h-12 px-8 rounded-full bg-[#6366F1] text-white font-bold text-sm hover:bg-white hover:text-[#080B10] transition-all">
                {c.cta_btn}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </section>

          <div className="pt-8">
            <Link to="/insights" data-testid="insight-back-link"
                  className="inline-flex items-center gap-2 text-sm text-[#64748B] hover:text-white transition-colors">
              <ChevronLeft className="h-4 w-4" />
              {c.back}
            </Link>
          </div>
        </div>
      </article>
    </div>
  );
}
